import React, { createContext, useCallback, useContext, useEffect, useImperativeHandle, useMemo, forwardRef, useRef, useState } from 'react';
import type { Editor, Extension, Node, Mark } from '@tiptap/core';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TiptapTable from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import type { ResponsiveStyleValue } from '@mui/system';
import {
  Box,
  Button,
  IconButton,
  Divider,
  Tooltip,
  Typography,
  useTheme,
  alpha,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import CodeIcon from '@mui/icons-material/Code';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import TableChartIcon from '@mui/icons-material/TableChart';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ImageIcon from '@mui/icons-material/Image';
import BrushIcon from '@mui/icons-material/Brush';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import LinkIcon from '@mui/icons-material/Link';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import { FileEntity } from '../../types';
import { filesService } from '../../services/filesService';
import type { FileApi } from '../../services/sandboxService';
import { fileIdFromPreviewUrl, useImageVersion, withImageVersion } from '../../utils/imageVersionStore';
import WordPageBreakOverlay from './WordPageBreakOverlay';
import WordFindingLeadBlock from './WordFindingLeadBlock';
import WordImageSizeToggle from './WordImageSizeToggle';
import { useWordMeasurer } from './useWordMeasurer';
import { WordCodeOnlyParagraphs, WORD_CODE_ONLY_CLASS } from './wordCodeOnlyParagraphs';
import { PageBreak } from './pageBreak';
import { FindingRefCatalogContext, SchwachstelleRef } from './schwachstelleRef';
import { formatFindingRef, type FindingReferenceOption } from '../../utils/schwachstelleReferenz';
import {
  parseWordImageSize,
  WORD_IMAGE_SIZE_DEFAULT,
  WORD_PAGE,
  wordImageSizePx,
  wordImageWidthFraction,
  type WordFindingLeadChrome,
  type WordImageSize,
} from './wordPageLayout';
import './wordFonts.css';

export type WordFindingLead = WordFindingLeadChrome;

/** TipTap-Image-Attribute inkl. Word-Preset (`data-word-size`). */
export type InlineImageAttrs = {
  src: string;
  alt?: string;
  title?: string;
  wordSize?: WordImageSize;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    inlineImage: {
      setImage: (options: InlineImageAttrs) => ReturnType;
    };
  }
}

export interface TipTapEditorHandle {
  insertImage: (url: string, alt?: string, wordSize?: WordImageSize) => void;
  insertCodeBlock: (content: string) => void;
  insertText: (text: string) => void;
}

/** Ergebnis einer Bildbearbeitung: gespeicherte Datei-ID (neu oder gleich), null = abgebrochen. */
export type EditImageResult = { fileId: number; inserted: boolean; wordSize?: WordImageSize } | null;

/**
 * Oeffnet den Bild-Annotations-Editor fuer eine Datei. `allowInsert` = der Aufrufer
 * darf zusaetzlich "Speichern & einfügen" anbieten (nicht bei Bildern, die bereits im Text stehen).
 */
export type EditImageHandler = (
  fileId: number,
  options: { allowInsert: boolean; wordSize?: WordImageSize },
) => Promise<EditImageResult>;

interface TipTapEditorProps {
  content: string;
  onChange?: (html: string) => void;
  editable?: boolean;
  placeholder?: string;
  /** Mindesthoehe des Inhaltsbereichs (px oder CSS-Laenge, z. B. `calc(100vh - 360px)`). */
  minHeight?: number | string;
  label?: string;
  labelAction?: React.ReactNode;
  testId?: string;
  onFocus?: () => void;
  onInsertImageClick?: () => void;
  galleryImages?: FileEntity[];
  onInsertImageFromGallery?: (fileId: number) => void;
  /** Alternative Datei-API fuer Galerie-Vorschauen (isolierte Umgebung). Default: filesService. */
  fileApi?: FileApi;
  /** Bild aus Zwischenablage (Strg+V): Upload, Rückgabe Preview-URL für &lt;img&gt; */
  onPasteImage?: (file: File) => Promise<string | null>;
  /** Bild im Text / in der Fokus-Seitenleiste bearbeiten (Annotations-Editor). */
  onEditImage?: EditImageHandler;
  /** Zusaetzliche TipTap-Extensions (z. B. domaenenspezifische Nodes). */
  extraExtensions?: Array<Extension | Node | Mark>;
  /**
   * Optionaler `top`-Wert fuer `position: sticky` der Toolbar (z. B. wenn eine zweite
   * sticky Leiste unter der AppBar liegt). Standard: Hoehe der fixen AppBar.
   */
  stickyToolbarTop?: ResponsiveStyleValue<string | number>;
  /** Fokusmodus (Vollbild) in der Toolbar anbieten. Standard: true. */
  allowFocusMode?: boolean;
  /**
   * Demo-/Präsentationsansicht: Overlay-Linien und der TipTap-Node
   * „Manueller Seitenumbruch“ bleiben unsichtbar. Bearbeiten und Fokusmodus
   * zeigen sie weiter. Der Break bleibt im HTML und im Word-Export.
   */
  hidePageBreaks?: boolean;
  /**
   * Detailbeschreibung: Finding-Kopf (Titel, Tabelle, Common) wie
   * `fill_schwachstelle_template` in die erste Seite einrechnen.
   */
  wordFindingLead?: WordFindingLead;
  /**
   * Andere Schwachstellen desselben Audits, in Report-Reihenfolge.
   * Gesetzt: Toolbar-Dropdown „Referenz“. Die Node speichert nur die ID.
   */
  findingReferences?: FindingReferenceOption[];
  findingReferenceLang?: 'de' | 'en';
  /** Die aktuelle Schwachstelle erscheint nicht im Dropdown. */
  excludeFindingId?: number | null;
}

/* ── Image NodeView (echte Bilder inline) ───────────────────── */

interface ImageNodeContextValue {
  editable: boolean;
  fileApi: FileApi;
  onEditImage?: EditImageHandler;
  /** Abstand der klebenden Bild-Aktionsleiste zum oberen Rand des Scroll-Containers. */
  actionBarTop: number;
  /** Fokusmodus: Bildbreite wie Word-Export (Satzspiegel × Preset). */
  wordPagePreview: boolean;
}

const ImageNodeContext = createContext<ImageNodeContextValue>({
  editable: false,
  fileApi: filesService,
  actionBarTop: 8,
  wordPagePreview: false,
});

const ImageNodeView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes, deleteNode }) => {
  const theme = useTheme();
  const ctx = useContext(ImageNodeContext);
  const src: string = node.attrs.src || '';
  const alt: string = node.attrs.alt || '';
  const wordSize = parseWordImageSize(node.attrs.wordSize);
  const fileId = fileIdFromPreviewUrl(src);
  const version = useImageVersion(fileId);
  const [hover, setHover] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setNatural(null);
  }, [src, version]);

  const applyNatural = (img: HTMLImageElement) => {
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    }
  };

  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete) applyNatural(img);
  }, [src, version]);

  const wordBox = natural
    ? wordImageSizePx(natural.w, natural.h, wordSize)
    : { width: wordImageSizePx(1, 1, wordSize).width, height: 0 };

  const handleEdit = async () => {
    if (!fileId || !ctx.onEditImage || busy) return;
    setBusy(true);
    try {
      const result = await ctx.onEditImage(fileId, { allowInsert: false, wordSize });
      if (!result) return;
      const patch: Partial<Pick<InlineImageAttrs, 'src' | 'wordSize'>> = {};
      if (result.fileId !== fileId) {
        patch.src = ctx.fileApi.getPreviewUrl(result.fileId);
      }
      if (result.wordSize) patch.wordSize = parseWordImageSize(result.wordSize);
      if (Object.keys(patch).length) updateAttributes(patch);
    } finally {
      setBusy(false);
    }
  };

  const setWordSize = (size: WordImageSize) => updateAttributes({ wordSize: size });

  const keepToolbar = (event: React.MouseEvent | React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const showSizeBar = ctx.editable && !failed && (ctx.wordPagePreview || hover || selected);
  const showHoverActions = ctx.editable && !failed && (hover || selected);
  const canEdit = Boolean(fileId && ctx.onEditImage);

  return (
    <NodeViewWrapper
      as="span"
      className="tiptap-image-node"
      data-drag-handle
      data-word-size={wordSize}
      data-word-height-pt={ctx.wordPagePreview && wordBox.height > 0 ? (wordBox.height * 0.75).toFixed(2) : undefined}
      data-testid="inline-image-node"
      style={
        ctx.wordPagePreview
          ? {
              /* Word: Bild ist ein eigener Absatz (jc left), darunter Descender + 6 pt Abstand. */
              display: 'block',
              width: wordBox.width,
              maxWidth: '100%',
              position: 'relative',
              margin: `0 0 ${WORD_PAGE.bodyAfterPt}pt 0`,
              paddingBottom: WORD_PAGE.imageDescentPx,
            }
          : {
              display: 'inline-block',
              width: `${wordImageWidthFraction(wordSize) * 100}%`,
              maxWidth: '100%',
              position: 'relative',
              verticalAlign: 'top',
              margin: '0.35em 0',
            }
      }
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/*
        Aktionsleiste bleibt gemountet (Fokusmodus immer, sonst Hover/Selection).
        Nicht zwischen Bildern umhängen — das hat Hover-Pingpong und Layoutsprung erzeugt.
      */}
      {showSizeBar && (
        <span
          contentEditable={false}
          data-testid="inline-image-toolbar"
          onMouseDown={keepToolbar}
          onPointerDown={keepToolbar}
          style={{
            display: 'block',
            position: 'sticky',
            top: ctx.actionBarTop,
            height: 0,
            textAlign: 'right',
            zIndex: 3,
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              padding: 3,
              margin: '8px 8px 0 0',
              borderRadius: 8,
              backgroundColor: '#1b2233',
              color: '#f2f5fb',
              border: '1px solid rgba(255,255,255,0.22)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.45)',
              pointerEvents: 'auto',
            }}
          >
            <WordImageSizeToggle
              compact
              value={wordSize}
              onChange={setWordSize}
              testId="inline-image-size"
            />
            {showHoverActions && canEdit && (
              <Tooltip title="Bild bearbeiten (Rahmen, Pfeile, Verpixeln, Zuschneiden …)" arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleEdit}
                    disabled={busy}
                    data-testid="inline-image-edit"
                    sx={{ color: '#f2f5fb' }}
                  >
                    <BrushIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {showHoverActions && (
              <Tooltip title="Bild aus dem Text entfernen (Datei bleibt in der Galerie)" arrow>
                <IconButton size="small" onClick={() => deleteNode()} data-testid="inline-image-remove" sx={{ color: '#ff8a80' }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </span>
        </span>
      )}
      {failed ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            borderRadius: 6,
            border: `1px dashed ${theme.palette.divider}`,
            color: theme.palette.text.secondary,
            fontSize: '0.8rem',
          }}
          title={src}
        >
          <BrokenImageOutlinedIcon sx={{ fontSize: 18 }} />
          Bild nicht verfügbar{fileId ? ` (#${fileId})` : ''}
        </span>
      ) : (
        <img
          ref={imgRef}
          src={withImageVersion(src, version)}
          alt={alt}
          onError={() => setFailed(true)}
          onLoad={(e) => {
            setFailed(false);
            applyNatural(e.currentTarget);
          }}
          draggable={false}
          style={{
            display: 'block',
            width: '100%',
            height: ctx.wordPagePreview && wordBox.height > 0 ? wordBox.height : 'auto',
            borderRadius: ctx.wordPagePreview ? 0 : 6,
            outline: selected && ctx.editable ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
            outlineOffset: 2,
            boxShadow: ctx.wordPagePreview
              ? 'none'
              : theme.palette.mode === 'dark' ? '0 1px 6px rgba(0,0,0,0.5)' : '0 1px 6px rgba(0,0,0,0.12)',
          }}
        />
      )}
      {ctx.editable && failed && (
        <span contentEditable={false} style={{ marginLeft: 6, verticalAlign: 'middle' }}>
          <Tooltip title="Verweis aus dem Text entfernen" arrow>
            <IconButton size="small" onClick={() => deleteNode()} data-testid="inline-image-remove" sx={{ color: theme.palette.error.main }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </span>
      )}
    </NodeViewWrapper>
  );
};

const InlineImageExtension = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      wordSize: {
        default: WORD_IMAGE_SIZE_DEFAULT,
        parseHTML: (element: HTMLElement) =>
          parseWordImageSize(element.getAttribute('data-word-size')),
        renderHTML: (attributes: { wordSize?: unknown }) => ({
          'data-word-size': parseWordImageSize(attributes.wordSize),
        }),
      },
    };
  },
  addCommands() {
    return {
      setImage:
        (options: InlineImageAttrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              src: options.src,
              alt: options.alt,
              title: options.title,
              wordSize: parseWordImageSize(options.wordSize),
            },
          }),
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

/* ── Toolbar Button ────────────────────────────────────────── */

interface ToolbarButtonProps {
  icon: React.ReactElement;
  tooltip: string;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ icon, tooltip, onClick, isActive, disabled, 'data-testid': dataTestId }) => {
  const theme = useTheme();

  return (
    <Tooltip title={tooltip} arrow>
      <span>
        <IconButton
          data-testid={dataTestId}
          size="small"
          onClick={onClick}
          disabled={disabled}
          sx={{
            borderRadius: 1,
            p: 0.5,
            color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
            bgcolor: isActive ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
            '&:hover': {
              bgcolor: isActive
                ? alpha(theme.palette.primary.main, 0.2)
                : alpha(theme.palette.text.primary, 0.08),
            },
          }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};

const TipTapEditor = forwardRef<TipTapEditorHandle, TipTapEditorProps>(({
  content,
  onChange,
  editable = true,
  placeholder = '',
  minHeight = 150,
  label,
  labelAction,
  testId,
  onFocus,
  onInsertImageClick,
  galleryImages = [],
  onInsertImageFromGallery,
  fileApi,
  onPasteImage,
  onEditImage,
  extraExtensions,
  stickyToolbarTop,
  allowFocusMode = true,
  hidePageBreaks = false,
  wordFindingLead,
  findingReferences,
  findingReferenceLang = 'de',
  excludeFindingId = null,
}, ref) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [imageMenuAnchorEl, setImageMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [refMenuAnchorEl, setRefMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [sidebarBusyId, setSidebarBusyId] = useState<number | null>(null);
  const [wordFlowEl, setWordFlowEl] = useState<HTMLElement | null>(null);
  const wordLang = wordFindingLead?.lang === 'en' ? 'en' : 'de';
  const wordMeasure = useWordMeasurer(wordLang);
  const onPasteImageRef = useRef(onPasteImage);
  const editableRef = useRef(editable);
  const editorRef = useRef<Editor | null>(null);
  const effectiveFileApi = fileApi ?? filesService;

  useEffect(() => {
    onPasteImageRef.current = onPasteImage;
  }, [onPasteImage]);
  useEffect(() => {
    editableRef.current = editable;
  }, [editable]);

  /* Fokusmodus: nur im Bearbeitungsmodus; Body-Scroll sperren, Esc beendet. */
  useEffect(() => {
    if (!editable && fullscreen) setFullscreen(false);
  }, [editable, fullscreen]);

  useEffect(() => {
    if (!fullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        setFullscreen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [fullscreen]);

  /* Position der klebenden Bild-Aktionsleiste: unterhalb der (sticky) Toolbar. */
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [toolbarHeight, setToolbarHeight] = useState(44);
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const update = () => setToolbarHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [editable, fullscreen]);
  const resolvedStickyTop = (() => {
    const v: unknown = stickyToolbarTop ?? { xs: 56, sm: 64 };
    if (typeof v === 'number') return v;
    if (typeof v === 'string') return parseInt(v, 10) || 64;
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      const candidate = o.sm ?? o.xs ?? o.md;
      return typeof candidate === 'number' ? candidate : 64;
    }
    return 64;
  })();
  const actionBarTop = fullscreen || !editable ? 8 : resolvedStickyTop + toolbarHeight + 8;

  const handleSidebarEdit = useCallback(
    async (fileId: number) => {
      if (!onEditImage || sidebarBusyId !== null) return;
      setSidebarBusyId(fileId);
      try {
        await onEditImage(fileId, { allowInsert: true });
      } finally {
        setSidebarBusyId(null);
      }
    },
    [onEditImage, sidebarBusyId],
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TiptapTable.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({
        placeholder,
      }),
      InlineImageExtension.configure({ inline: true, allowBase64: false }),
      WordCodeOnlyParagraphs,
      PageBreak,
      SchwachstelleRef,
      ...(extraExtensions ?? []),
    ],
    content,
    editable,
    editorProps: {
      handlePaste(_view, event) {
        if (!editableRef.current) return false;
        const upload = onPasteImageRef.current;
        if (!upload) return false;
        const cd = event.clipboardData;
        if (!cd) return false;
        let imageFile: File | null = null;
        for (const item of Array.from(cd.items)) {
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const f = item.getAsFile();
            if (f) {
              imageFile = f;
              break;
            }
          }
        }
        if (!imageFile) return false;
        event.preventDefault();
        void (async () => {
          try {
            const url = await upload(imageFile!);
            const ed = editorRef.current;
            if (url && ed && editableRef.current) {
              ed.chain().focus().setImage({ src: url, alt: '', wordSize: WORD_IMAGE_SIZE_DEFAULT }).run();
            }
          } catch (e) {
            console.error('TipTap paste image upload failed:', e);
          }
        })();
        return true;
      },
    },
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
    onFocus: () => {
      if (onFocus) {
        onFocus();
      }
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useImperativeHandle(ref, () => ({
    insertImage: (url: string, alt?: string, wordSize?: WordImageSize) => {
      if (editor) {
        const selection = editor.state.selection;
        const { from, empty } = selection;
        // Editor noch nie fokussiert (Auswahl am Dokumentanfang): ans Ende einfuegen statt vor den ersten Absatz
        const untouched = !editor.isFocused && empty && from <= 1;
        // Ist gerade ein Knoten (z. B. ein Bild) ausgewaehlt, wuerde setImage ihn ersetzen –
        // stattdessen dahinter einfuegen.
        const nodeSelected = 'node' in selection && Boolean((selection as { node?: unknown }).node);
        const focusAt = untouched ? 'end' : nodeSelected ? selection.to : undefined;
        editor.chain().focus(focusAt).setImage({
          src: url,
          alt: alt || '',
          wordSize: parseWordImageSize(wordSize),
        }).run();
      }
    },
    insertCodeBlock: (content: string) => {
      if (editor) {
        const text = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        editor.chain().focus().insertContent({
          type: 'codeBlock',
          content: text ? [{ type: 'text', text }] : [],
        }).run();
      }
    },
    insertText: (text: string) => {
      if (editor && text) {
        editor.chain().focus().insertContent(text).run();
      }
    },
  }), [editor]);

  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);

  const findingRefCatalog = useMemo(() => {
    const byId = new Map<number, FindingReferenceOption>();
    for (const item of findingReferences ?? []) byId.set(item.id, item);
    return { byId, lang: findingReferenceLang };
  }, [findingReferences, findingReferenceLang]);
  const findingRefOptions = (findingReferences ?? []).filter((item) => item.id !== excludeFindingId);

  if (!editor) {
    return null;
  }

  const borderColor = isDark ? alpha(theme.palette.divider, 0.3) : theme.palette.divider;
  const imageMenuOpen = Boolean(imageMenuAnchorEl);

  const handleOpenImageMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (onInsertImageClick) onInsertImageClick();
    setImageMenuAnchorEl(event.currentTarget);
  };

  const handleCloseImageMenu = () => {
    setImageMenuAnchorEl(null);
  };

  const handleSelectGalleryImage = (fileId: number) => {
    if (onInsertImageFromGallery) onInsertImageFromGallery(fileId);
    setImageMenuAnchorEl(null);
  };

  const contentMinHeight: string | number = minHeight;
  const proseMinHeight: string | number =
    typeof minHeight === 'number' ? Math.max(0, minHeight - 24) : `calc(${minHeight} - 24px)`;
  const galleryImageEntries = galleryImages.filter((f) => /\.(png|jpe?g|gif|webp|svg)$/i.test(f.dateiname));

  return (
    <ImageNodeContext.Provider value={{ editable, fileApi: effectiveFileApi, onEditImage, actionBarTop, wordPagePreview: fullscreen }}>
    <FindingRefCatalogContext.Provider value={findingRefCatalog}>
    <Box>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              display: 'block',
            }}
          >
            {label}
          </Typography>
          {labelAction && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {labelAction}
            </Box>
          )}
        </Box>
      )}
      {/*
        Rahmen: im Fokusmodus wird nur das Styling auf `position: fixed` umgestellt.
        Die DOM-Struktur bleibt identisch, damit die Editor-Instanz nicht neu gemountet wird.
      */}
      <Box
        data-testid={testId ? `${testId}-frame` : undefined}
        data-fullscreen={fullscreen ? 'true' : undefined}
        data-hide-page-breaks={hidePageBreaks ? 'true' : undefined}
        sx={
          fullscreen
            ? {
                position: 'fixed',
                inset: 0,
                zIndex: theme.zIndex.modal - 1,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: theme.palette.background.default,
              }
            : {
                border: `1px solid ${borderColor}`,
                borderRadius: 1,
                /* nicht `hidden`: wuerde sticky Toolbar am Viewport verhindern */
                overflow: 'visible',
                '&:focus-within': editable
                  ? {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
                    }
                  : {},
              }
        }
      >
        {/* Kopfzeile nur im Fokusmodus */}
        <Box
          sx={{
            display: fullscreen ? 'flex' : 'none',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            borderBottom: `1px solid ${borderColor}`,
            bgcolor: theme.palette.background.paper,
            flexShrink: 0,
          }}
        >
          <FullscreenIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }} noWrap>
            {label ? `${label} – Fokusmodus` : 'Fokusmodus'}
          </Typography>
          {labelAction}
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
            Esc beendet den Fokusmodus · Änderungen werden wie gewohnt mit „Speichern“ übernommen
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FullscreenExitIcon />}
            onClick={() => setFullscreen(false)}
            data-testid={testId ? `${testId}-btn-focus-exit` : undefined}
          >
            Fokusmodus beenden
          </Button>
        </Box>

        {/* Toolbar - only in edit mode */}
        {editable && (
          <Box
            ref={toolbarRef}
            data-testid={testId ? `${testId}-toolbar` : undefined}
            sx={{
              position: fullscreen ? 'static' : 'sticky',
              top: stickyToolbarTop ?? { xs: 56, sm: 64 },
              zIndex: (theme.zIndex.appBar ?? 1100) - 2,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.25,
              p: 0.5,
              px: fullscreen ? 2 : 0.5,
              flexShrink: 0,
              borderBottom: `1px solid ${borderColor}`,
              bgcolor: isDark ? alpha(theme.palette.background.paper, 0.92) : alpha(theme.palette.grey[100], 0.92),
              backdropFilter: 'blur(8px)',
            }}
          >
            <ToolbarButton
              icon={<UndoIcon fontSize="small" />}
              tooltip="Rückgängig"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
            />
            <ToolbarButton
              icon={<RedoIcon fontSize="small" />}
              tooltip="Wiederholen"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <ToolbarButton
              data-testid={testId ? `${testId}-btn-bold` : undefined}
              icon={<FormatBoldIcon fontSize="small" />}
              tooltip="Fett"
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive('bold')}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-italic` : undefined}
              icon={<FormatItalicIcon fontSize="small" />}
              tooltip="Kursiv"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive('italic')}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-underline` : undefined}
              icon={<FormatUnderlinedIcon fontSize="small" />}
              tooltip="Unterstrichen"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive('underline')}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-strikethrough` : undefined}
              icon={<StrikethroughSIcon fontSize="small" />}
              tooltip="Durchgestrichen"
              onClick={() => editor.chain().focus().toggleStrike().run()}
              isActive={editor.isActive('strike')}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <ToolbarButton
              data-testid={testId ? `${testId}-btn-h1` : undefined}
              icon={<Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: 1 }}>H1</Typography>}
              tooltip="Überschrift 1"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              isActive={editor.isActive('heading', { level: 1 })}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-h2` : undefined}
              icon={<Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: 1 }}>H2</Typography>}
              tooltip="Überschrift 2"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              isActive={editor.isActive('heading', { level: 2 })}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-h3` : undefined}
              icon={<Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: 1 }}>H3</Typography>}
              tooltip="Überschrift 3"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              isActive={editor.isActive('heading', { level: 3 })}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <ToolbarButton
              data-testid={testId ? `${testId}-btn-bullet-list` : undefined}
              icon={<FormatListBulletedIcon fontSize="small" />}
              tooltip="Aufzählung"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive('bulletList')}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-ordered-list` : undefined}
              icon={<FormatListNumberedIcon fontSize="small" />}
              tooltip="Nummerierte Liste"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={editor.isActive('orderedList')}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-blockquote` : undefined}
              icon={<FormatQuoteIcon fontSize="small" />}
              tooltip="Zitat"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              isActive={editor.isActive('blockquote')}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-code` : undefined}
              icon={<CodeIcon fontSize="small" />}
              tooltip="Inline Code"
              onClick={() => editor.chain().focus().toggleCode().run()}
              isActive={editor.isActive('code')}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-code-block` : undefined}
              icon={<IntegrationInstructionsIcon fontSize="small" />}
              tooltip="Code-Block"
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
              isActive={editor.isActive('codeBlock')}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <ToolbarButton
              data-testid={testId ? `${testId}-btn-align-left` : undefined}
              icon={<FormatAlignLeftIcon fontSize="small" />}
              tooltip="Links ausrichten"
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              isActive={editor.isActive({ textAlign: 'left' })}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-align-center` : undefined}
              icon={<FormatAlignCenterIcon fontSize="small" />}
              tooltip="Zentrieren"
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              isActive={editor.isActive({ textAlign: 'center' })}
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-align-right` : undefined}
              icon={<FormatAlignRightIcon fontSize="small" />}
              tooltip="Rechts ausrichten"
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              isActive={editor.isActive({ textAlign: 'right' })}
            />

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <ToolbarButton
              data-testid={testId ? `${testId}-btn-table` : undefined}
              icon={<TableChartIcon fontSize="small" />}
              tooltip="Tabelle einfügen (3x3)"
              onClick={() =>
                editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
              }
            />
            <ToolbarButton
              data-testid={testId ? `${testId}-btn-page-break` : undefined}
              icon={<InsertPageBreakIcon fontSize="small" />}
              tooltip="Seitenumbruch einfügen (Strg+Enter) — nächster Block oben auf der nächsten Word-Seite"
              onClick={() => editor.chain().focus().setPageBreak().run()}
            />

            {findingReferences && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Tooltip
                  title={
                    findingRefOptions.length === 0
                      ? 'Keine andere Schwachstelle in diesem Audit'
                      : 'Schwachstelle aus diesem Audit referenzieren'
                  }
                  arrow
                >
                  <span>
                    <Button
                      data-testid={testId ? `${testId}-btn-finding-ref` : 'btn-finding-ref'}
                      size="small"
                      variant="outlined"
                      startIcon={<LinkIcon fontSize="small" />}
                      disabled={findingRefOptions.length === 0}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => setRefMenuAnchorEl(event.currentTarget)}
                      sx={{ fontWeight: 700, textTransform: 'none', lineHeight: 1.4 }}
                    >
                      Referenz
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}

            {onInsertImageClick && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Tooltip title="Bild aus Galerie einfügen" arrow>
                  <span>
                    <IconButton
                      data-testid={testId ? `${testId}-btn-insert-image` : undefined}
                      size="small"
                      onClick={handleOpenImageMenu}
                      disabled={!onInsertImageFromGallery}
                      sx={{
                        borderRadius: 1,
                        p: 0.5,
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.text.primary, 0.08),
                        },
                      }}
                    >
                      <ImageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {onPasteImage && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, display: { xs: 'none', lg: 'inline' } }}>
                Strg+V fügt Bilder aus der Zwischenablage ein
              </Typography>
            )}

            {allowFocusMode && !fullscreen && (
              <>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Fokusmodus: Editor bildschirmfüllend mit Word-Seitenumbrüchen" arrow>
                  <Button
                    data-testid={testId ? `${testId}-btn-focus` : undefined}
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<FullscreenIcon />}
                    onClick={() => setFullscreen(true)}
                    sx={{ fontWeight: 700, ml: 0.5 }}
                  >
                    Fokusmodus
                  </Button>
                </Tooltip>
              </>
            )}
          </Box>
        )}

        <Menu
          anchorEl={refMenuAnchorEl}
          open={Boolean(refMenuAnchorEl)}
          onClose={() => setRefMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              // Lange Labels `{nummer} – {klasse} – {titel}` sollen in einer Zeile lesbar sein.
              minWidth: 'min(640px, calc(100vw - 32px))',
              width: 'min(720px, calc(100vw - 32px))',
              maxWidth: 'min(720px, calc(100vw - 32px))',
              maxHeight: 420,
              overflowY: 'auto',
            },
          }}
        >
          {findingRefOptions.map((item) => (
            <MenuItem
              key={item.id}
              data-testid={`finding-ref-option-${item.id}`}
              onClick={() => {
                editor.chain().focus().insertSchwachstelleRef(item.id).run();
                setRefMenuAnchorEl(null);
              }}
            >
              <ListItemText
                primary={formatFindingRef(item, findingReferenceLang)}
                sx={{ width: '100%', minWidth: 0, my: 0 }}
                primaryTypographyProps={{
                  sx: {
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </MenuItem>
          ))}
        </Menu>

        <Menu
          anchorEl={imageMenuAnchorEl}
          open={imageMenuOpen}
          onClose={handleCloseImageMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { width: 380, maxHeight: 420 } }}
        >
          {galleryImages.length === 0 ? (
            <MenuItem disabled>
              <ListItemText
                primary="Keine Bilder in der Galerie"
                secondary="Bitte zuerst einen Screenshot hochladen."
              />
            </MenuItem>
          ) : (
            galleryImages.map((file) => (
              <MenuItem key={file.id} onClick={() => handleSelectGalleryImage(file.id)} sx={{ py: 1.25 }}>
                <Box
                  component="img"
                  src={effectiveFileApi.getPreviewUrl(file.id)}
                  alt={file.dateiname}
                  sx={{
                    width: 88,
                    height: 88,
                    objectFit: 'cover',
                    borderRadius: 1,
                    mr: 1.25,
                    border: `1px solid ${borderColor}`,
                    flexShrink: 0,
                  }}
                />
                <ListItemText
                  primary={file.dateiname}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: '0.85rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              </MenuItem>
            ))
          )}
        </Menu>

        {/* Body: Inhalt + (Fokusmodus) Seitenleiste */}
        <Box
          sx={
            fullscreen
              ? { display: 'flex', flex: 1, minHeight: 0 }
              : { display: 'block' }
          }
        >
        {/* Editor Content */}
        <Box
          data-testid={testId ? `${testId}-content` : undefined}
          onClick={fullscreen ? (e) => { if (e.target === e.currentTarget) editor.commands.focus('end'); } : undefined}
          sx={{
            minHeight: fullscreen ? 0 : contentMinHeight,
            p: fullscreen ? 0 : 1.5,
            ...(fullscreen
              ? {
                  flex: 1,
                  minWidth: 0,
                  overflowY: 'auto',
                  px: 2,
                  py: 2,
                  cursor: 'text',
                  bgcolor: theme.palette.background.default,
                }
              : {}),
            '& .ProseMirror': {
              outline: 'none',
              minHeight: fullscreen ? 'calc(100vh - 260px)' : proseMinHeight,
              color: theme.palette.text.primary,
              fontSize: '0.95rem',
              lineHeight: 1.7,
              '& p': {
                margin: '0.25em 0',
              },
              '& .node-schwachstelleRef, & .stark-schwachstelle-ref': {
                display: 'inline',
              },
              '& h1': {
                fontSize: '1.5rem',
                fontWeight: 700,
                margin: '0.5em 0 0.25em',
                color: theme.palette.text.primary,
              },
              '& h2': {
                fontSize: '1.25rem',
                fontWeight: 600,
                margin: '0.5em 0 0.25em',
                color: theme.palette.text.primary,
              },
              '& h3': {
                fontSize: '1.1rem',
                fontWeight: 600,
                margin: '0.5em 0 0.25em',
                color: theme.palette.text.primary,
              },
              '& ul': {
                paddingLeft: `${WORD_PAGE.listIndentPx}px`,
                margin: '0.25em 0',
                listStyleType: 'square',
                listStylePosition: 'outside',
              },
              '& ol': {
                paddingLeft: `${WORD_PAGE.listIndentPx}px`,
                margin: '0.25em 0',
                listStyleType: 'square',
                listStylePosition: 'outside',
              },
              '& li': {
                margin: '0.1em 0',
              },
              '& blockquote': {
                borderLeft: `3px solid ${theme.palette.primary.main}`,
                paddingLeft: '1em',
                margin: '0.5em 0',
                color: theme.palette.text.secondary,
                fontStyle: 'italic',
              },
              '& code': {
                backgroundColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.06),
                borderRadius: '3px',
                padding: '0.15em 0.3em',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                fontSize: '0.9em',
              },
              /* Ein zusammenhaengender Codeblock (wie Word-Console), kein Zeilenstapel. */
              '& pre': {
                display: 'block',
                boxSizing: 'border-box',
                whiteSpace: 'pre-wrap',
                tabSize: 4,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                fontSize: '0.85rem',
                lineHeight: 1.45,
                color: theme.palette.text.primary,
                backgroundColor: isDark ? '#12162b' : '#e8ecf2',
                border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.12)}`,
                borderRadius: '6px',
                padding: '0.85em 1em',
                margin: '0.75em 0',
                overflow: 'auto',
                '& code': {
                  display: 'block',
                  backgroundColor: 'transparent',
                  padding: 0,
                  borderRadius: 0,
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  whiteSpace: 'inherit',
                  color: 'inherit',
                },
              },
              /* Aufeinanderfolgende Einzeilen-<pre> optisch zu einem Block ziehen. */
              '& pre + pre': {
                borderTop: 'none',
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                marginTop: 0,
                paddingTop: 0,
              },
              '& pre:has(+ pre)': {
                borderBottom: 'none',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                marginBottom: 0,
                paddingBottom: 0,
              },
              /* Mehrere Zeilen nur-Inline-Code wie einen Block darstellen (Word-Export). */
              /* Reine Code-Absaetze (Plugin-Klasse; :only-child uebersieht Textknoten neben <code>). */
              [`& p.${WORD_CODE_ONLY_CLASS}`]: {
                margin: 0,
                padding: '0 1em',
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                fontSize: '0.85rem',
                backgroundColor: isDark ? '#12162b' : '#e8ecf2',
                borderLeft: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.12)}`,
                borderRight: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.12)}`,
                '& code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                  borderRadius: 0,
                  fontFamily: 'inherit',
                  fontSize: 'inherit',
                },
              },
              [`& p.${WORD_CODE_ONLY_CLASS}:first-child, & :not(p.${WORD_CODE_ONLY_CLASS}) + p.${WORD_CODE_ONLY_CLASS}`]: {
                marginTop: '0.75em',
                paddingTop: '0.85em',
                borderTop: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.12)}`,
                borderTopLeftRadius: '6px',
                borderTopRightRadius: '6px',
              },
              [`& p.${WORD_CODE_ONLY_CLASS}:not(:has(+ p.${WORD_CODE_ONLY_CLASS}))`]: {
                marginBottom: '0.75em',
                paddingBottom: '0.85em',
                borderBottom: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#000', 0.12)}`,
                borderBottomLeftRadius: '6px',
                borderBottomRightRadius: '6px',
              },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                margin: '0.5em 0',
                '& td, & th': {
                  border: `1px solid ${borderColor}`,
                  padding: '0.5em',
                  minWidth: '80px',
                },
                '& th': {
                  backgroundColor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.04),
                  fontWeight: 600,
                },
              },
              /* Bilder: echte Darstellung via ImageNodeView (max. Breite des Editors) */
              '& .tiptap-image-node': {
                maxWidth: '100%',
              },
              '& .stark-page-break': hidePageBreaks
                ? {
                    display: 'none',
                  }
                : {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    height: '22px',
                    margin: '10px 0',
                    userSelect: 'none',
                    textAlign: 'left',
                    '&::before': {
                      content: '""',
                      flex: 1,
                      borderTop: '2px dashed #c45c26',
                    },
                    '&::after': {
                      content: '"Manueller Seitenumbruch"',
                      flexShrink: 0,
                      px: 0.75,
                      py: 0.15,
                      bgcolor: '#c45c26',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      borderRadius: '3px',
                      lineHeight: 1.4,
                      fontFamily: theme.typography.fontFamily,
                    },
                  },
              '& .ProseMirror-selectednode.stark-page-break': hidePageBreaks
                ? {}
                : {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                    borderRadius: '4px',
                  },
              '& p.is-editor-empty:first-of-type::before': {
                content: 'attr(data-placeholder)',
                float: 'left',
                color: theme.palette.text.disabled,
                pointerEvents: 'none',
                height: 0,
              },
              ...(fullscreen
                ? {
                    /*
                     * Word-Typografie des Reports (wordPageGeometry.json): Arial Narrow 12 pt,
                     * 13.8 pt Zeile, 6 pt nach Absatz, Blocksatz, kein Kerning. Die Umbruchlinie
                     * rechnet Word nach (wordLineBreaker) — die Darstellung hier folgt so nah wie
                     * CSS es erlaubt (CSS staucht Wortabstände nicht).
                     */
                    minHeight: WORD_PAGE.contentHeightPx,
                    color: theme.palette.text.primary,
                    fontFamily: WORD_PAGE.bodyFont,
                    fontSize: `${WORD_PAGE.bodySizePt}pt`,
                    lineHeight: `${WORD_PAGE.bodyLinePt}pt`,
                    textAlign: 'justify',
                    fontKerning: 'none',
                    fontVariantLigatures: 'none',
                    textRendering: 'geometricPrecision',
                    hyphens: 'none',
                    overflowWrap: 'break-word',
                    '& p': {
                      margin: `0 0 ${WORD_PAGE.bodyAfterPt}pt 0`,
                      textAlign: 'justify',
                      lineHeight: `${WORD_PAGE.bodyLinePt}pt`,
                    },
                    '& h1': {
                      fontSize: `${WORD_PAGE.h1SizePt}pt`,
                      fontWeight: 700,
                      margin: `0 0 ${WORD_PAGE.bodyAfterPt}pt 0`,
                      color: theme.palette.text.primary,
                      lineHeight: `${WORD_PAGE.h1LinePt}pt`,
                      textAlign: 'justify',
                    },
                    '& h2': {
                      fontSize: `${WORD_PAGE.h2SizePt}pt`,
                      fontWeight: 700,
                      margin: `0 0 ${WORD_PAGE.bodyAfterPt}pt 0`,
                      color: theme.palette.text.primary,
                      lineHeight: `${WORD_PAGE.h2LinePt}pt`,
                      textAlign: 'justify',
                    },
                    '& h3': {
                      fontSize: `${WORD_PAGE.h3SizePt}pt`,
                      fontWeight: 700,
                      margin: `0 0 ${WORD_PAGE.bodyAfterPt}pt 0`,
                      color: theme.palette.text.primary,
                      lineHeight: `${WORD_PAGE.h3LinePt}pt`,
                      textAlign: 'justify',
                    },
                    /* MS-Listenabsatz: jeder Punkt ist ein Body-Text-Absatz (6 pt danach), Einzug 720 twips. */
                    '& ul, & ol': {
                      paddingLeft: WORD_PAGE.listIndentPx,
                      margin: 0,
                      listStyleType: 'square',
                      listStylePosition: 'outside',
                    },
                    '& li': { margin: 0, padding: 0 },
                    '& li > p': { margin: `0 0 ${WORD_PAGE.bodyAfterPt}pt 0` },
                    '& blockquote': {
                      borderLeft: 'none',
                      paddingLeft: WORD_PAGE.blockquoteIndentPx,
                      margin: 0,
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                    },
                    /* Inline-Code: Consolas 8 pt ohne Innenabstand (Word kennt kein Padding). */
                    '& code': {
                      backgroundColor: isDark ? alpha('#fff', 0.1) : alpha('#000', 0.08),
                      borderRadius: 0,
                      padding: 0,
                      fontFamily: WORD_PAGE.codeFont,
                      fontSize: `${WORD_PAGE.codeSizePt}pt`,
                      lineHeight: 1,
                      color: theme.palette.text.primary,
                      /* Ohne lokales Consolas: Liberation Mono (0.6 em) auf Consolas-Dickte (0.5498 em) ziehen. */
                      letterSpacing: wordMeasure.codeFallback ? `${-(0.6001 - 0.5498) * WORD_PAGE.codeSizePt}pt` : 0,
                    },
                    /* Reiner Code-Absatz: Word-Zeile = Consolas-Höhe 9.37 pt, Marke zählt nicht. */
                    [`& p.${WORD_CODE_ONLY_CLASS}`]: {
                      margin: `0 0 ${WORD_PAGE.bodyAfterPt}pt 0`,
                      padding: 0,
                      border: 'none',
                      borderRadius: 0,
                      fontFamily: WORD_PAGE.codeFont,
                      fontSize: `${WORD_PAGE.codeSizePt}pt`,
                      lineHeight: `${WORD_PAGE.codeLinePt}pt`,
                      backgroundColor: 'transparent',
                      whiteSpace: 'pre-wrap',
                      textAlign: 'justify',
                      '& code': {
                        lineHeight: 'inherit',
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                      },
                    },
                    /* Console-Style: Courier New 9 pt, 1.15 Zeilen = 11.72 pt, kein Absatzabstand, Einzug 227 twips. */
                    '& pre': {
                      fontFamily: WORD_PAGE.consoleFont,
                      fontSize: `${WORD_PAGE.consoleSizePt}pt`,
                      lineHeight: `${WORD_PAGE.consoleLinePt}pt`,
                      color: theme.palette.text.primary,
                      backgroundColor: isDark ? alpha('#fff', 0.05) : alpha('#000', 0.05),
                      border: 'none',
                      borderRadius: 0,
                      padding: 0,
                      margin: `0 0 0 ${WORD_PAGE.consoleIndentPx}px`,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'break-word',
                      textAlign: 'justify',
                      '& code': {
                        backgroundColor: 'transparent',
                        padding: 0,
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                        lineHeight: 'inherit',
                        letterSpacing: 0,
                        color: 'inherit',
                      },
                    },
                    '& pre + pre': { marginTop: 0, paddingTop: 0 },
                    '& pre:has(+ pre)': { marginBottom: 0, paddingBottom: 0 },
                    /* Tabellen aus dem Editor: Zellenabsatz Standard (kein Abstand), Zellenrand 108 twips. */
                    '& table': {
                      fontSize: `${WORD_PAGE.bodySizePt}pt`,
                      lineHeight: `${WORD_PAGE.bodyLinePt}pt`,
                      margin: 0,
                      width: '100%',
                      tableLayout: 'fixed',
                      '& td, & th': {
                        border: `0.5pt solid ${isDark ? alpha('#fff', 0.35) : alpha('#000', 0.6)}`,
                        padding: '0 5.4pt',
                        minWidth: 0,
                        color: theme.palette.text.primary,
                        verticalAlign: 'top',
                      },
                      '& td p, & th p': { margin: 0 },
                      '& th': {
                        backgroundColor: 'transparent',
                        fontWeight: 700,
                      },
                    },
                    '& hr': {
                      border: 'none',
                      borderBottom: `0.75pt solid #999`,
                      height: `${WORD_PAGE.bodyLinePt + 1}pt`,
                      margin: `0 0 ${WORD_PAGE.bodyAfterPt}pt 0`,
                    },
                    '& .tiptap-image-node': {
                      maxWidth: WORD_PAGE.contentWidthPx,
                    },
                    '& .stark-page-break': hidePageBreaks
                      ? { display: 'none' }
                      : {
                          textAlign: 'left',
                          fontFamily: theme.typography.fontFamily,
                          lineHeight: 1.4,
                          height: '22px',
                          margin: '8px 0',
                        },
                    '& p.is-editor-empty:first-of-type::before': {
                      color: theme.palette.text.disabled,
                    },
                  }
                : {}),
            },
          }}
        >
          <Box
            data-testid={testId ? `${testId}-word-page` : undefined}
            sx={
              fullscreen
                ? {
                    width: WORD_PAGE.contentWidthPx,
                    maxWidth: '100%',
                    mx: 'auto',
                    mt: 3,
                    position: 'relative',
                    /* Gleiche Fläche wie der Fokus-Chrome — keine schwebende Seite/Karte. */
                    bgcolor: 'transparent',
                    color: theme.palette.text.primary,
                    boxShadow: 'none',
                    borderRadius: 0,
                    px: 0,
                    py: 0,
                  }
                : undefined
            }
          >
            <WordPageBreakOverlay
              editor={editor}
              enabled={fullscreen && !hidePageBreaks}
              flowRoot={wordFlowEl}
              measurer={wordMeasure.measurer}
              geometry={wordMeasure.geometry}
              fontEpoch={wordMeasure.fontEpoch}
            />
            <Box ref={setWordFlowEl} data-testid={testId ? `${testId}-word-flow` : undefined}>
              {fullscreen && wordFindingLead && (
                <WordFindingLeadBlock lead={wordFindingLead} measurer={wordMeasure.measurer} geometry={wordMeasure.geometry} />
              )}
              <EditorContent editor={editor} />
            </Box>
          </Box>
        </Box>

        {/* Seitenleiste: Screenshots (nur Fokusmodus) */}
        <Box
          data-testid={testId ? `${testId}-focus-sidebar` : undefined}
          sx={{
            display: fullscreen && editable ? 'flex' : 'none',
            flexDirection: 'column',
            width: 300,
            flexShrink: 0,
            borderLeft: `1px solid ${borderColor}`,
            bgcolor: theme.palette.background.paper,
            minHeight: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${borderColor}` }}>
            <PhotoLibraryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
              Screenshots
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {galleryImageEntries.length}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
            {galleryImageEntries.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Noch keine Screenshots. Bild in den Editor einfügen mit Strg+V oder unten in der Galerie hochladen.
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {galleryImageEntries.map((file) => (
              <SidebarImageTile
                key={file.id}
                file={file}
                previewUrl={effectiveFileApi.getPreviewUrl(file.id)}
                borderColor={borderColor}
                onInsert={onInsertImageFromGallery ? () => onInsertImageFromGallery(file.id) : undefined}
                onEdit={onEditImage ? () => handleSidebarEdit(file.id) : undefined}
                busy={sidebarBusyId === file.id}
              />
            ))}
            </Box>
          </Box>
          {onPasteImage && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, borderTop: `1px solid ${borderColor}` }}>
              Tipp: Screenshot in die Zwischenablage kopieren und mit Strg+V direkt an der Cursorposition einfügen.
            </Typography>
          )}
        </Box>
        </Box>
      </Box>
    </Box>
    </FindingRefCatalogContext.Provider>
    </ImageNodeContext.Provider>
  );
});

/* ── Seitenleisten-Kachel (Fokusmodus) ─────────────────────── */

interface SidebarImageTileProps {
  file: FileEntity;
  previewUrl: string;
  borderColor: string;
  onInsert?: () => void;
  onEdit?: () => void;
  busy?: boolean;
}

const SidebarImageTile: React.FC<SidebarImageTileProps> = ({ file, previewUrl, borderColor, onInsert, onEdit, busy }) => {
  const theme = useTheme();
  const version = useImageVersion(file.id);
  const isEdited = Boolean(file.original_file_id || file.annotationen);
  return (
    <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: 1.5, overflow: 'hidden', flexShrink: 0 }}>
      <Box
        component="img"
        src={withImageVersion(previewUrl, version)}
        alt={file.dateiname}
        sx={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', bgcolor: alpha(theme.palette.text.primary, 0.04) }}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, px: 1, py: 0.75 }}>
        <Typography
          variant="caption"
          title={file.dateiname}
          sx={{
            flex: 1,
            minWidth: 0,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            lineHeight: 1.35,
          }}
        >
          {isEdited && (
            <BrushIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle', color: theme.palette.primary.main }} />
          )}
          {file.dateiname}
        </Typography>
        {(onInsert || onEdit) && (
          <Box sx={{ display: 'flex', flexShrink: 0, mt: -0.25 }}>
            {onInsert && (
              <Tooltip title="An Cursorposition einfügen" arrow>
                <IconButton size="small" onClick={onInsert} sx={{ color: theme.palette.primary.main }} data-testid={`focus-insert-${file.id}`}>
                  <InsertPhotoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onEdit && (
              <Tooltip title={isEdited ? 'Bearbeitung anpassen' : 'Bearbeiten (Rahmen, Pfeile, Verpixeln …)'} arrow>
                <span>
                  <IconButton size="small" onClick={onEdit} disabled={busy} data-testid={`focus-edit-${file.id}`}>
                    <BrushIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

TipTapEditor.displayName = 'TipTapEditor';

export default TipTapEditor;
